"use client";

import React, { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useGame } from "@/app/providers/GameContextProvider";
import { useTranslation } from "@/hooks/useTranslation";
import { useGameQuality } from '@/app/providers/GameQualityProvider';
import { GameMode } from "@/lib/types/common/GameMode";
import { saveDailyScore } from "@/lib/utils/dailyUtils";
import { GoldenText, GrayText } from "./StyledText";
import { useAudio, AUDIO } from '@/app/providers/AudioProvider';

interface WinnerScreenProps {
    onViewGameRecap: () => void;
}

const WinnerScreen = ({ onViewGameRecap }: WinnerScreenProps) => {
    const { gameState, room } = useGame();
    const { t } = useTranslation();
    const { getBackdropBlurIntensity } = useGameQuality();
    const { play } = useAudio();

    const animationDelay = 0;
    const darkBackdropDelay = animationDelay;
    const winnerDelay = darkBackdropDelay + 0.4;
    const placeDelay = winnerDelay + 0.4;
    const buttonsDelay = placeDelay + 0.5;

    const isEndlessMode = gameState?.gameMode === GameMode.ENDLESS ||
        (gameState?.gameMode === GameMode.CUSTOM && gameState?.gameSettings?.baseMode === GameMode.ENDLESS);
    const isDailyMode = gameState?.gameMode === GameMode.DAILY;
    const isFightMode = gameState?.gameMode === GameMode.FIGHT ||
        (gameState?.gameMode === GameMode.CUSTOM && gameState?.gameSettings?.baseMode === GameMode.FIGHT);
    const isCoopMode = gameState?.gameMode === GameMode.COOP ||
        (gameState?.gameMode === GameMode.CUSTOM && gameState?.gameSettings?.baseMode === GameMode.COOP);
    const isTeamMode = isFightMode || isCoopMode;

    // Get current player
    const currentPlayer = useMemo(() => {
        return gameState?.players?.get(room?.sessionId || "");
    }, [gameState, room?.sessionId]);

    // Get winning team info for Fight mode or Coop mode
    // Use teamRankings snapshot if available (preserves results even when teams change)
    const winningTeamInfo = useMemo(() => {
        if (!isTeamMode || !gameState) return null;

        let winningTeamData: { id: string; name: string; color: string; totalScore: number; currentHp: number; maxHp: number } | null = null;
        
        // Prefer using teamRankings snapshot (rank 1 = winner)
        if (gameState.teamRankings && gameState.teamRankings.length > 0) {
            const winnerRanking = gameState.teamRankings.find(t => t.rank === 1);
            if (winnerRanking) {
                winningTeamData = {
                    id: winnerRanking.teamId,
                    name: winnerRanking.name,
                    color: winnerRanking.color,
                    totalScore: winnerRanking.totalScore,
                    currentHp: winnerRanking.finalHp,
                    maxHp: winnerRanking.maxHp,
                };
            }
        } else {
            // Fallback to live teams data
            let winningTeam;
            if (isFightMode) {
                // Fight mode: team with HP > 0 wins
                winningTeam = gameState.teams.find(team => team.currentHp > 0);
            } else if (isCoopMode) {
                // Coop mode: team with highest total score wins
                let highestScore = -1;
                for (const team of Array.from(gameState.teams)) {
                    if (team.totalScore > highestScore) {
                        highestScore = team.totalScore;
                        winningTeam = team;
                    }
                }
            }
            if (winningTeam) {
                winningTeamData = {
                    id: winningTeam.id,
                    name: winningTeam.name,
                    color: winningTeam.color,
                    totalScore: winningTeam.totalScore,
                    currentHp: winningTeam.currentHp,
                    maxHp: winningTeam.maxHp,
                };
            }
        }

        if (!winningTeamData) return null;

        // Get team members from finalRankings if available, otherwise from live players
        let teamMembers;
        if (gameState.finalRankings && gameState.finalRankings.length > 0) {
            // Use finalRankings to get members (teamId is stored in player's teamId property)
            teamMembers = Array.from(gameState.players?.entries() || [])
                .filter(([, p]) => p.teamId === winningTeamData!.id)
                .map(([sessionId, p]) => ({ ...p, sessionId }));
        } else {
            teamMembers = Array.from(gameState.players?.entries() || [])
                .filter(([, p]) => p.teamId === winningTeamData!.id)
                .map(([sessionId, p]) => ({ ...p, sessionId }));
        }

        return {
            team: winningTeamData,
            members: teamMembers,
        };
    }, [isTeamMode, isFightMode, isCoopMode, gameState]);

    // Save daily score when Daily mode game ends (on WinnerScreen mount)
    useEffect(() => {
        if (!gameState || !room) return;
        if (isDailyMode) {
            const player = gameState.players?.get(room.sessionId);
            if (player) {
                // Extract round scores from roundHistory
                const roundScores: number[] = [];
                gameState.roundHistory.forEach((round) => {
                    const playerResult = round.playerResults.get(room.sessionId);
                    if (playerResult && playerResult.scores && playerResult.scores.length >= round.roundNumber) {
                        roundScores.push(playerResult.scores[round.roundNumber - 1]);
                    }
                });
                
                saveDailyScore(player.totalScore, roundScores);
            }
        }
    }, [isDailyMode, gameState?.players, room?.sessionId]);

    if (!gameState || !room) return null;

    // Use finalRankings if available (preserves rankings even when players leave)
    // Otherwise fallback to live players data (for compatibility during development)
    let playerRankings;

    let playerRankingsWithRank: { sessionId: string; name: string; totalScore: number; rank: number }[];

    if (gameState.finalRankings && gameState.finalRankings.length > 0) {
        // Use the snapshot created when game ended (already has ex aequo handled)
        playerRankingsWithRank = Array.from(gameState.finalRankings).map(ranking => ({
            sessionId: ranking.sessionId,
            name: ranking.name,
            totalScore: isEndlessMode ? ranking.currentStreak : ranking.totalScore,
            rank: ranking.rank,
        }));
        playerRankings = playerRankingsWithRank;
    } else {
        // Fallback to computing from live players data (old behavior) with ex aequo
        const sorted = Array.from(gameState.players?.entries() || [])
            .map(([sessionId, player]) => ({
                sessionId,
                name: player.name,
                totalScore: isEndlessMode ? player.currentStreak : player.totalScore,
                rank: 0,
            }))
            .sort((a, b) => b.totalScore - a.totalScore);
        
        // Handle ex aequo
        let currentRankVal = 1;
        let previousScore: number | null = null;
        playerRankingsWithRank = sorted.map((p, index) => {
            if (previousScore !== null && p.totalScore !== previousScore) {
                currentRankVal = index + 1;
            }
            previousScore = p.totalScore;
            return { ...p, rank: currentRankVal };
        });
        playerRankings = playerRankingsWithRank;
    }

    // Get all winners (players with rank 1, handles ex aequo)
    const winners = playerRankingsWithRank.filter(p => p.rank === 1);
    const winnerNames = winners.map(w => w.name).join(" & ");
    const winner = isTeamMode ? winningTeamInfo?.team : { ...winners[0], name: winnerNames };
    const currentPlayerRanking = playerRankingsWithRank.find(p => p.sessionId === room.sessionId);
    const currentRank = currentPlayerRanking?.rank || 0;
    const isWinner = isTeamMode
        ? currentPlayer?.teamId === winningTeamInfo?.team.id
        : currentRank === 1;
    const currentValue = isEndlessMode ? currentPlayer?.currentStreak || 0 : currentPlayer?.totalScore || 0;

    // Check if player is alone (solo mode)
    const isSoloPlayer = playerRankings.length === 1;

    if (!currentPlayer) return null;


    const handleBackToLobby = () => {
        room.send("backToLobby");
    };


    return (
        <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className={`absolute inset-0 flex items-center justify-center z-20 ${getBackdropBlurIntensity()}`}
        >
            <div className="w-full text-center space-y-8">
                {/* Winner Title */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: darkBackdropDelay }}
                    className="w-full space-y-3 bg-slate-900/30 py-5 md:py-10 relative"
                >
                    {/* Solo player view - simplified */}
                    {isSoloPlayer && !isTeamMode && (
                        <motion.div
                            initial={{ x: -100, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ duration: 0.5, delay: winnerDelay }}
                            className="flex justify-center"
                        >
                            <GoldenText>
                                {isEndlessMode ? (
                                    <div className="flex items-center gap-4 justify-center">
                                        <span>{t("play.streak")} {currentValue}</span>
                                    </div>
                                ) : (
                                    currentValue + " points"
                                )}
                            </GoldenText>
                        </motion.div>
                    )}

                    {/* Multiplayer view - original content */}
                    {!isSoloPlayer && (
                        <>
                            <motion.div
                                initial={{ x: -100, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ duration: 0.5, delay: winnerDelay }}
                            >
                                {isWinner ? (
                                    <GoldenText>
                                        {t("play.winner.title", { playerName: winner?.name || '' })}
                                    </GoldenText>
                                ) : (
                                    <GrayText>
                                        {t("play.winner.title", { playerName: winner?.name || '' })}
                                    </GrayText>
                                )}
                            </motion.div>

                            {isTeamMode && (
                                <motion.div
                                    initial={{ x: -100, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ duration: 0.5, delay: placeDelay }}
                                    className={`${(winningTeamInfo?.members.length || 0) >= 4 ? "grid  grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-2 md:gap-x-[50px]" : "flex gap-5 md:gap-x-[50px]"} justify-center mx-auto items-center`}
                                >
                                    {winningTeamInfo?.members.map(member => (
                                        member.uniqueId === currentPlayer.uniqueId ? (
                                            <GoldenText key={member.sessionId} size="medium">
                                                {member.name}
                                            </GoldenText>
                                        ) : (
                                            <GrayText key={member.sessionId} size="medium">
                                                {member.name}
                                            </GrayText>
                                        )
                                    ))}
                                </motion.div>
                            )}

                            {!isTeamMode && (
                                <motion.div
                                    initial={{ x: -100, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ duration: 0.5, delay: placeDelay }}
                                    className="flex gap-10 md:gap-[100px] justify-center"
                                >
                                    <GoldenText>
                                        {"#" + currentRank + " place"}
                                    </GoldenText>

                                    <GoldenText>
                                        {isEndlessMode ? (
                                            <div className="flex items-center gap-4 justify-center">
                                                <span>{t("play.streak")} {currentValue}</span>
                                            </div>
                                        ) : (
                                            currentValue + " points"
                                        )}
                                    </GoldenText>
                                </motion.div>
                            )}
                        </>
                    )}
                </motion.div>

                {/* Action Buttons */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: buttonsDelay }}
                    className="flex flex-col gap-2 2xl:gap-6 justify-center w-full items-center"
                >
                    <div
                        className="group relative font-eurostar text-[#F1EBC8] text-shadow shadow-[#2D1705] uppercase cursor-pointer w-fit px-20"
                        onMouseEnter={() => play(AUDIO.HOVER)}
                        onClick={() => {
                            play(AUDIO.CLICK);
                            onViewGameRecap();
                        }}
                    >
                        <span className="relative z-[2] text-2xl md:text-3xl 2xl:text-4xl">{t("play.gameRecapButton")}</span>
                        <div
                            className="absolute z-[1] top-0 left-0 w-full bg-gradient-to-b from-[#BF9474]/0 via-[#CDA042]/70 via-[50%] to-[#BF9474]/0 h-full blur-[1px] opacity-0 group-hover:opacity-100 duration-100"
                            style={{
                                maskImage: 'linear-gradient(to right, transparent 0%, black 20%, black 80%, transparent 100%)'
                            }}>
                        </div>
                    </div>
                    <div
                        className="group relative font-eurostar text-[#F1EBC8] text-shadow shadow-[#2D1705] uppercase cursor-pointer w-fit px-20"
                        onMouseEnter={() => play(AUDIO.HOVER)}
                        onClick={() => {
                            play(AUDIO.CLICK);
                            handleBackToLobby();
                        }}
                    >
                        <span className="relative z-[2] text-2xl md:text-3xl 2xl:text-4xl">{t("play.backToLobbyButton")}</span>
                        <div
                            className="absolute z-[1] top-0 left-0 w-full bg-gradient-to-b from-[#BF9474]/0 via-[#CDA042]/70 via-[50%] to-[#BF9474]/0 h-full blur-[1px] opacity-0 group-hover:opacity-100 duration-100"
                            style={{
                                maskImage: 'linear-gradient(to right, transparent 0%, black 20%, black 80%, transparent 100%)'
                            }}>
                        </div>
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default WinnerScreen;